import { useLayoutEffect, type RefObject } from 'react'

import { canCrossfade } from '@/lib/motion/motion-preference'
import { SKELETON_SWAP_MS } from '@/lib/motion/skeleton'

/**
 * How long an in-place refresh may run before it says so: the delay inside the
 * refresh line's fade-in (`--motion-refresh-delay`). Most land inside it, and
 * those show nothing but their new data.
 */
const REFRESH_LINE_DELAY_MS = 300

/** The line's fade-out once the data has landed: `--motion-med`. */
const REFRESH_LINE_OUT_MS = 220

/**
 * The thin progress line along a refreshing region's top edge (`refresh-region`
 * in globals.css), driven by `data-refresh` on `ref`:
 *
 *  - `busy` while `updating`: the line sweeps, and fades in only once the wait
 *    passes 300ms, so a quick refresh never shows it;
 *  - `leaving` for 220ms after, while it fades out — only when it had shown;
 *  - absent otherwise, and the line with it.
 *
 * Written to the element directly rather than through React state: it changes
 * nothing React renders, and a state change would re-render the whole region —
 * a table, a chart — twice per refresh to move one pseudo-element. A layout
 * effect, so the line starts leaving in the frame the new data first paints.
 */
export function useRefreshLine(ref: RefObject<HTMLElement | null>, updating: boolean): void {
  useLayoutEffect(() => {
    const region = ref.current
    if (region === null) return
    if (updating) {
      const startedAt = performance.now()
      region.setAttribute('data-refresh', 'busy')
      return () => {
        if (performance.now() - startedAt < REFRESH_LINE_DELAY_MS) {
          region.removeAttribute('data-refresh')
          return
        }
        region.setAttribute('data-refresh', 'leaving')
        window.setTimeout(() => {
          if (region.getAttribute('data-refresh') === 'leaving')
            region.removeAttribute('data-refresh')
        }, REFRESH_LINE_OUT_MS)
      }
    }
    return undefined
  }, [ref, updating])
}

/**
 * The region as it stands, for `crossfadeArrival` to fade out over the data
 * replacing it: a deep copy, its canvases' pixels copied over too (a cloned
 * canvas is blank, and a chart is drawn on one). Taken in the commit that
 * brings the new data, before React writes it — `getSnapshotBeforeUpdate` —
 * so it is exactly what was on screen. Null where the copy could not animate:
 * no Web Animations (jsdom) or reduced motion, where the swap is a cut.
 */
export function snapshotRegion(region: HTMLElement): HTMLElement | null {
  if (!canCrossfade()) return null
  const copy = region.cloneNode(true) as HTMLElement
  const sources = region.querySelectorAll('canvas')
  const targets = copy.querySelectorAll('canvas')
  sources.forEach((source, index) => {
    const target = targets[index]
    if (target === undefined || source.width === 0 || source.height === 0) return
    target.getContext('2d')?.drawImage(source, 0, 0)
  })
  return copy
}

/**
 * Crosses `region`'s new content with `copy`, the old one (`snapshotRegion`):
 * the copy, inert and hidden, goes over the region and fades out in 160ms
 * while every other child fades in over 220ms (`refresh-leaving` and
 * `data-refresh-arrived` in globals.css) — the skeleton swap's crossfade,
 * with the old data where the skeleton would be. Nothing moves: the copy
 * takes the region's own box and layout.
 *
 * The copy goes in first, ahead of React's own children: a rule keyed on the
 * last child (`divide-y`, `space-y-*`, `last:`) is far more common than one
 * on the first, and one added at the end would restyle the region's last row
 * for the length of the fade.
 */
export function crossfadeArrival(region: HTMLElement, copy: HTMLElement): void {
  for (const attribute of ['id', 'aria-busy', 'aria-live', 'role', 'data-refresh']) {
    copy.removeAttribute(attribute)
  }
  copy.querySelectorAll('[id]').forEach((element) => {
    element.removeAttribute('id')
  })
  copy.setAttribute('aria-hidden', 'true')
  copy.setAttribute('data-refresh-leaving', '')
  copy.inert = true
  copy.classList.add('refresh-leaving')

  region.prepend(copy)
  region.setAttribute('data-refresh-arrived', '')
  window.setTimeout(() => {
    copy.remove()
    region.removeAttribute('data-refresh-arrived')
  }, SKELETON_SWAP_MS)
}
